import { useEffect, useId, useState } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
} from '@speaking-coach/ui'

import type { OperatorFaqDiagram } from './registry'

export function MermaidDiagram({
  diagram,
}: {
  diagram: OperatorFaqDiagram
}): JSX.Element {
  const reactId = useId()
  const [svg, setSvg] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    const id = `operator-faq-${reactId.replaceAll(':', '')}`

    void import('mermaid')
      .then(async ({ default: mermaid }) => {
        // Mermaid derives shades from concrete colors, so it reads the theme's values.
        const root = getComputedStyle(document.documentElement)
        const token = (name: string) => root.getPropertyValue(name).trim()
        const [inset, border, text, line] = ['--color-surface-card-inset', '--color-border-default', '--color-text-primary', '--color-text-secondary'].map(token)
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          themeVariables: {
            fontFamily: 'inherit',
            primaryColor: inset,
            primaryBorderColor: border,
            primaryTextColor: text,
            lineColor: line,
            actorBkg: inset,
            actorBorder: line,
            actorTextColor: text,
            signalColor: line,
            signalTextColor: text,
          },
        })
        const rendered = await mermaid.render(id, diagram.definition)
        if (live) setSvg(rendered.svg)
      })
      .catch(() => {
        if (live) setFailed(true)
      })

    return () => {
      live = false
    }
  }, [diagram.definition, reactId])

  if (failed) {
    return (
      <p className="rounded-token-card bg-surface-app p-3 text-caption text-text-secondary">
        The diagram could not be displayed. The instructions above remain the
        authoritative workflow.
      </p>
    )
  }

  return (
    <figure className="space-y-2">
      <div className="relative">
        <div
          aria-label={diagram.caption}
          className="min-h-32 overflow-x-auto rounded-token-card border border-border-default bg-surface-app p-3 pr-12 [&_svg]:min-w-[34rem] [&_svg]:max-w-none"
          // Mermaid is loaded only with reviewed definitions from registry.ts;
          // no API, form, or user string reaches this renderer.
          dangerouslySetInnerHTML={svg === null ? undefined : { __html: svg }}
        >
          {svg === null ? (
            <p className="text-caption text-text-secondary">Loading diagram…</p>
          ) : null}
        </div>
        {svg !== null ? (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                aria-label="Expand diagram"
                className="absolute right-2 top-2 bg-surface-card shadow-card-hi"
              >
                <Icon name="maximize" />
              </Button>
            </DialogTrigger>
            <DialogContent className="h-[calc(100vh-2rem)]">
              <DialogHeader>
                <DialogTitle>Workflow diagram</DialogTitle>
                <DialogDescription>{diagram.caption}</DialogDescription>
              </DialogHeader>
              <div className="flex min-h-0 flex-1 overflow-auto bg-surface-app p-4 sm:p-8">
                <div
                  aria-label={`Expanded: ${diagram.caption}`}
                  className="m-auto w-full min-w-[52rem] max-w-[80rem] [&_svg]:h-auto [&_svg]:w-full [&_svg]:max-w-none"
                  dangerouslySetInnerHTML={{ __html: svg }}
                />
              </div>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>
      <figcaption className="text-caption text-text-secondary">
        {diagram.caption}
      </figcaption>
    </figure>
  )
}
